/**
 * 行政进销存 角色/权限常量与判定
 * 依据《行政进销存系统开发需求文档.md》第 3 章（角色定义与权限矩阵）、第 6 章（审批流程规则）
 *
 * 说明：
 * - 角色以 sessionStorage.userRoleId 为准，特殊账号以 sessionStorage.uid 为准；
 * - roleId = 1 为超级管理员（admin），拥有全部权限；
 * - 所有判定仅用于前端菜单/tab 显隐与交互限制，真正的审批路由与数据权限以后端为准；
 * - 保持现有后端契约（usageType、状态值等不变），本文件只收敛“谁能看到什么/谁能操作什么”。
 */

const getRoleId = () => String(sessionStorage.getItem('userRoleId') || '')
const getUid = () => String(sessionStorage.getItem('uid') || '')

// 角色 roleId 常量（文档 3.1.1，不可改动）
export const ROLE = {
  SALES: '2', // 销售
  CUSTOMER_MANAGER: '3', // 客户经理
  GENERAL_MANAGER: '8', // 总经理
  FINANCE: '27', // 财务
  ADMIN_STAFF: '31', // 行政部（行政人员）
  ADMIN_DIRECTOR: '48', // 行政总监
  CTO: '54' // CTO
}

// 超级管理员（roleId = 1，拥有全部权限）
export const isSuperAdmin = () => getRoleId() === '1'

// 销售类角色（2、3）——可发起请购/申领/自采，且仅销售可选自己名下商机（文档 3.1.2 / 4.3）
export const SALES_ROLE_IDS = ['2', '3']

// 技术部门负责人（审批链最终到 CTO 54，文档 3.1.2 / 6.1 / 6.2）
export const TECH_DEPT_LEADER_ROLE_IDS = [
  '6', '26', '32', '34', '35', '36', '38', '42', '43', '44', '46', '49'
]

// 非销售部门负责人（审批链到总经理 8，文档 3.1.2 / 6.1 / 6.2）
export const NON_SALES_DEPT_LEADER_ROLE_IDS = ['29', '48', '28', '27', '50']

// 销售条线事业部总监（按 userID 识别：政企92 / 医疗民生99 / 市场拓展141，文档 3.1.2 / 6.1）
export const SALES_DIRECTOR_UIDS = ['92', '99', '141']

// 部门负责人组（可审批下属请购/申领，文档 3.2 注2）
export const DEPT_LEADER_ROLE_IDS = [
  ...TECH_DEPT_LEADER_ROLE_IDS,
  ...NON_SALES_DEPT_LEADER_ROLE_IDS
]

// 是否为销售类角色（2、3），超级管理员视为具备
export const isSales = () => isSuperAdmin() || SALES_ROLE_IDS.includes(getRoleId())

// 是否免审批直通：总经理账号（角色 8）或陈总个人账号（userID=96）或超级管理员（roleId=1），请购/申领/采购单均免审批（文档 6.5.1 / Q9）
export const isExempt = () => isSuperAdmin() || getRoleId() === ROLE.GENERAL_MANAGER || getUid() === '96'

// 是否为行政（行政总监 48 / 行政部 31）
export const isAdmin = () =>
  isSuperAdmin() || getRoleId() === ROLE.ADMIN_DIRECTOR || getRoleId() === ROLE.ADMIN_STAFF

// 可发起采购单（文档 3.2 / 5.4）
export const canCreatePurchase = () => isAdmin()

// 可审批采购单：财务 27 / 总经理 8 / 行政总监 48（复核行政人员 31 创建的采购单，文档 6.3）
export const canApprovePurchase = () =>
  isSuperAdmin() ||
  [ROLE.FINANCE, ROLE.GENERAL_MANAGER, ROLE.ADMIN_DIRECTOR].includes(getRoleId())

// 可审批请购/申领：部门负责人组 + 总经理 8 + CTO 54 + 销售事业部总监（uid 92/99/141）
export const canApproveRequest = () => {
  if (isSuperAdmin()) return true
  const role = getRoleId()
  if (role === ROLE.GENERAL_MANAGER || role === ROLE.CTO) return true
  if (DEPT_LEADER_ROLE_IDS.includes(role)) return true
  if (SALES_DIRECTOR_UIDS.includes(getUid())) return true
  return false
}

// 可确认出库（申领出库 / 请购直接出库）/ 入库：行政总监 48 / 行政部 31（文档 3.2 / 5.5 / 8.2）
export const canOutbound = () => isAdmin()

// 固定渠道：创建/管理（行政部 31 / 行政总监 48，文档 3.2 / 5.1）
export const canManageFixedChannel = () => isAdmin()

// 固定渠道：审批（财务 27，文档 3.2 / 5.1）
export const canApproveFixedChannel = () =>
  isSuperAdmin() || getRoleId() === ROLE.FINANCE

// 个人自采（自采单）：发起（销售 2、3，文档 3.2 / 5.2）
export const canCreatePersonalPurchase = () => isSales()

// 个人自采：审批（事业部总监 uid 92/99/141；业务发展部销售/超5万 → 总经理 8，文档 6.4）
export const canApprovePersonalPurchase = () =>
  isSuperAdmin() || getRoleId() === ROLE.GENERAL_MANAGER || SALES_DIRECTOR_UIDS.includes(getUid())

// 付款单：查看/处理（财务 27，文档 3.2 / 9.8）
export const canProcessPayment = () =>
  isSuperAdmin() || getRoleId() === ROLE.FINANCE

export default {
  ROLE,
  SALES_ROLE_IDS,
  TECH_DEPT_LEADER_ROLE_IDS,
  NON_SALES_DEPT_LEADER_ROLE_IDS,
  SALES_DIRECTOR_UIDS,
  DEPT_LEADER_ROLE_IDS,
  isSuperAdmin,
  isSales,
  isExempt,
  isAdmin,
  canCreatePurchase,
  canApprovePurchase,
  canApproveRequest,
  canOutbound,
  canManageFixedChannel,
  canApproveFixedChannel,
  canCreatePersonalPurchase,
  canApprovePersonalPurchase,
  canProcessPayment
}
